import { useState } from 'react';

import {
  IconArchive,
  IconCertificate,
  IconComponents,
  IconCopy,
  IconLanguage,
  IconPrinter,
  IconTrash,
  IconUsersGroup,
  IconUsersPlus,
} from '@tabler/icons-react';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

import { AmplitudeProductEvent } from '@cfa/pathway-fe-shared';
import { Plan, PlanPermissions } from '@cfa/pathway-fe-shared/xp';
import { Divider } from '@cfa/react-components';
import { Show } from '@cfa/system-icons';

import { usePlanLocations } from '@/hooks/usePlanLocations';

import PopoverMenuButton from '@/components/PopoverMenuButton/PopoverMenuButton';
import PopoverMenuButtonItem from '@/components/PopoverMenuButton/PopoverMenuButtonItem';
import { printPlan } from '@/components/print-report';
import {
  PrintCertificateModals,
  usePrintCertificates,
} from '@/components/PrintCertificates';

import { useAmplitudeTrack } from '@/amplitude/useAmplitude';
import { trainTogetherModeDispatchAtom } from '@/atoms/trainTogetherMode';
import PlanInUseModal from '@/routes/TrainingPlans/ManagePlans/View/Modals/PlanInUseModal';

interface PlanCardMenuProps {
  assignedUsers: number;
  assignPlan: () => void;
  archivePlan: () => void;
  duplicatePlan: () => void;
  editTranslations: () => void;
  isReadonly?: boolean;
  navigateToEditPlan: () => void;
  plan: Plan;
  removePlan?: () => void;
  sendPlanToParent: () => void;
  permissions: Set<keyof PlanPermissions>;
}

interface DividerItem {
  type: 'divider';
}

interface ActionMenuItem {
  dataTestId: string;
  icon: React.ReactNode;
  onClick: () => void;
  text: string;
  permission?: keyof PlanPermissions;
  isDestructive?: boolean;
  showWhen?: boolean;
  type?: never;
}

type PlanMenuItem = ActionMenuItem | DividerItem;

const PlanCardMenu: React.FC<PlanCardMenuProps> = ({
  assignedUsers,
  assignPlan,
  archivePlan,
  duplicatePlan,
  editTranslations,
  isReadonly = false,
  navigateToEditPlan,
  plan,
  removePlan,
  sendPlanToParent,
  permissions,
}) => {
  const { t } = useTranslation();
  const track = useAmplitudeTrack();
  const sendTrainTogetherModeAction = useSetAtom(trainTogetherModeDispatchAtom);
  const { getLocationsByPlan } = usePlanLocations();
  const [showPlanInUseModal, setShowPlanInUseModal] = useState(false);

  const handleEditPlanClick = () => {
    if (!isReadonly && assignedUsers > 0) {
      setShowPlanInUseModal(true);
    } else {
      navigateToEditPlan();
    }
  };

  const certificateHook = usePrintCertificates({ planId: plan.id });

  const allMenuItems: PlanMenuItem[] = [
    {
      dataTestId: 'ThreeDotMenuAssign',
      icon: <IconUsersPlus />,
      onClick: () => assignPlan(),
      text: t('Generic.assign'),
      permission: 'assign',
    },
    {
      dataTestId: 'ThreeDotMenuTrainTogether',
      icon: <IconUsersGroup />,
      onClick: () => {
        track(AmplitudeProductEvent.SHOW_TRAIN_TOGETHER_MODE, {
          callSite: 'PlanCard',
          planId: plan.id,
          isComplianceOrAnchor:
            plan.type.toLowerCase() === 'compliance' ||
            plan.type.toLowerCase() === 'anchor',
          locations: getLocationsByPlan(plan),
        });
        sendTrainTogetherModeAction('showTrainTogetherMode');
        sendPlanToParent();
      },
      text: t('Generic.trainTogether'),
      permission: 'trainTogether',
    },
    { type: 'divider' },
    {
      dataTestId: 'ThreeDotMenuEditPlan',
      icon: <IconComponents />,
      onClick: handleEditPlanClick,
      text: t('Generic.editPlanContent'),
      permission: 'edit',
    },
    {
      dataTestId: 'ThreeDotMenuEditPlan',
      icon: <Show className="size-6!" />,
      onClick: () => navigateToEditPlan(),
      text: t('Generic.viewPlanContent'),
      // There is no 'view' permission, so show even if read-only
      permission: 'assign',
      showWhen: isReadonly,
    },
    {
      dataTestId: 'ThreeDotMenuRemovePlan',
      icon: <IconTrash />,
      onClick: () => removePlan?.(),
      text: t('TrainingPlans.manageThreeDotMenu.remove'),
      isDestructive: true,
      permission: 'remove',
    },
    {
      dataTestId: 'ThreeDotMenuManageTranslations',
      icon: <IconLanguage />,
      onClick: () => editTranslations(),
      text: t('Generic.editTranslations'),
      permission: 'edit',
    },
    {
      dataTestId: 'ThreeDotMenuDuplicatePlan',
      icon: <IconCopy />,
      onClick: () => duplicatePlan(),
      text: t('TrainingPlans.manageThreeDotMenu.duplicate'),
      permission: 'edit',
    },
    {
      dataTestId: 'ThreeDotMenuArchivePlan',
      icon: <IconArchive data-testid="PlanArchive" />,
      onClick: () => archivePlan(),
      text: t('TrainingPlans.manageThreeDotMenu.archive'),
      isDestructive: true,
      permission: 'archive',
    },
    { type: 'divider' },
    {
      dataTestId: 'ThreeDotMenuPrintReport',
      icon: <IconPrinter />,
      onClick: () => {
        printPlan(plan);
      },
      text: t('Button.printReport'),
    },
    {
      dataTestId: 'ThreeDotMenuGetCertificates',
      icon: <IconCertificate />,
      onClick: () => certificateHook.handleClick(),
      text: t('Button.getCertificates'),
      showWhen: certificateHook.shouldShow && !certificateHook.isLoading,
    },
  ];

  const renderPopoverButtons = (items: PlanMenuItem[]): React.ReactNode[] => {
    let prevButtonWasDivider = false;

    return items.reduce<React.ReactNode[]>((acc, curr, index: number) => {
      if ('type' in curr && curr.type === 'divider' && !prevButtonWasDivider) {
        prevButtonWasDivider = true;
        acc.push(
          <Divider
            key={`divider-${index}`}
            style={{ margin: '0.5em 0' }}
            variant="middle"
          />,
        );
      }

      if (!('type' in curr)) {
        const hasPermission =
          !curr.permission || permissions.has(curr.permission);

        const shouldShow = curr.showWhen === undefined || curr.showWhen;

        if (hasPermission && shouldShow) {
          prevButtonWasDivider = false;
          acc.push(
            <PopoverMenuButtonItem
              key={curr.dataTestId}
              dataTestId={curr.dataTestId}
              icon={curr.icon}
              onClick={curr.onClick}
              text={curr.text}
              isDestructive={curr.isDestructive}
            />,
          );
        }
      }
      return acc;
    }, []);
  };

  return (
    <>
      <PopoverMenuButton data-testid="PlanCardMenu">
        {renderPopoverButtons(allMenuItems)}
      </PopoverMenuButton>

      <PrintCertificateModals {...certificateHook} />

      <PlanInUseModal
        isOpen={showPlanInUseModal}
        onClose={() => setShowPlanInUseModal(false)}
        onContinue={() => {
          setShowPlanInUseModal(false);
          navigateToEditPlan();
        }}
      />
    </>
  );
};

export default PlanCardMenu;
